import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  ArrowLeft, Building2, FlaskConical, Minus, Plus, X,
} from 'lucide-react';

import AddressPicker from './AddressPicker';
import {
  checkShipmentAddress, loadProducts, loadShipmentAddressForm, loadShipmentCountries, loadShipmentLocations,
  loadShipmentPlaceParts, loadShipmentProductStock, searchShipmentAddresses,
} from './api';
import { SearchField, XomicsSelect } from './parts';
import { tx } from './i18n';
import ShippingComposer from './ShippingComposer';
import { EntryBoxes, countOf, isLeft } from './StoreStock';
import { Field, errorMessage, fieldAria } from './ui';

// Which ones of a product with choices go in the parcel (a ring's finish and
// size): how many of each combination, in a form of its own over the
// shipment's, never more of one than the office has. `picked`: how many of
// each are in the parcel now, by SKU; `onDone` gets the same, as it is left.
function WhichOnes({
  product, stock, picked, onClose, onDone,
}) {
  const [amounts, setAmounts] = useState(() => Object.fromEntries(
    Object.entries(picked).map(([sku, quantity]) => [sku, String(quantity)])));
  const [wrong, setWrong] = useState([]);
  const [error, setError] = useState('');
  const typed = stock.combinations.filter((row) => !isLeft(amounts[row.sku]));
  const inAll = typed.reduce((sum, row) => sum + (countOf(amounts[row.sku]) ?? 0), 0);

  function done(event) {
    event.preventDefault();
    const unreadable = typed.filter((row) => countOf(amounts[row.sku]) === null);
    if (unreadable.length > 0) {
      setWrong(unreadable.map((row) => row.sku));
      setError(tx('ui.store.stock.error.quantity'));
      return;
    }
    const short = typed.filter((row) => countOf(amounts[row.sku]) > row.stock);
    if (short.length > 0) {
      setWrong(short.map((row) => row.sku));
      setError(tx('ui.store.stock.error.not_enough_of', { count: short[0].stock.toLocaleString(), name: short[0].label }));
      return;
    }
    onDone(Object.fromEntries(typed.map((row) => [row.sku, countOf(amounts[row.sku])])));
  }

  return (
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form className="xo-pc-form is-entries" role="dialog" aria-modal="true" aria-labelledby="xo-which-title"
        onSubmit={done} onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pc-form-head">
          <span className="xo-pc-thumb">{product.mainImageThumbnailUrl ? <img src={product.mainImageThumbnailUrl} alt="" /> : null}</span>
          <div>
            <h2 id="xo-which-title">{product.name}</h2>
            <p>{tx('ui.shipments.which_hint')}</p>
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx('ui.common.cancel')} onClick={onClose}><X aria-hidden="true" /></button>
        </header>
        <div className="xo-pc-form-body">
          {/* An empty box shows how many the office has of it. */}
          <EntryBoxes stock={stock} amounts={amounts} wrong={wrong} shows
            onAmounts={(next) => { setWrong([]); setError(''); setAmounts(next); }} />
        </div>
        <footer className="xo-pc-form-foot">
          {error
            ? <p className="xo-alert t-bad" role="alert">{error}</p>
            : <span className="xo-stock-sum">{inAll > 0 ? tx('ui.store.stock.in_all', { count: inAll.toLocaleString() }) : ''}</span>}
          <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.common.cancel')}</button>
          <button type="submit" className="xo-btn">{tx('ui.shipments.which_done')}</button>
        </footer>
      </form>
    </div>
  );
}

// A country by name, in the portal's language.
function countryNamer() {
  let names = null;
  try { names = new Intl.DisplayNames([document.documentElement.lang || 'en', 'en'], { type: 'region' }); } catch { names = null; }
  return (code) => names?.of(code) || code;
}

// The countries of the address form by name.
function countryChoices(countries) {
  const nameOf = countryNamer();
  return countries
    .map((country) => ({ code: country.code, name: nameOf(country.code) }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

// One of the company's own locations can be sent to as it is: it has an
// email and a phone for the carrier, and a point on the map.
const reachable = (location) => Boolean(location.email) && Boolean(location.phone)
  && location.address.latitude !== null && location.address.longitude !== null;

// A freeform shipment: products sent to anyone, without an order, a step at a
// time.
//   1. The products (of a product with choices, which ones: a ring's finish
//      and size).
//   2. Where it goes, chosen from one list: a custom address, or one of the
//      company's other offices and laboratories.
// Then it is shipped like an order: box, return labels, carriers, quotes,
// label. Buying the label records it as a free order under the recipient's
// email, so it shows in their app and on /track.
//
// One of the company's own locations is sent to as the portal keeps it: its
// name, email, phone and address are shown, not asked for, and cannot be
// changed here.
//
// A custom address goes anywhere, and its form opens under the list. Its
// country comes first (every country, the office's own to begin with), then
// who gets it and the address line, which is searched as it is typed, in that
// country, and in the rest of the world when that country has nothing. A
// chosen suggestion fills the fields, its country too; every field can be
// typed or corrected. XOmics checks the address as it stands on going on.
export default function NewShipmentModal({ onClose, onCreated }) {
  // The step in sight: 'products' or 'destination'. A draft is the shipment
  // handed on to its carriers.
  const [step, setStep] = useState('products');
  const [products, setProducts] = useState(null);
  const [query, setQuery] = useState('');
  const [quantities, setQuantities] = useState({});
  // A product with choices: what the office has of each combination (read when
  // the product is first chosen), how many of each are in the parcel, by
  // product and SKU, and the product whose combinations are being chosen.
  const [stocks, setStocks] = useState({});
  const [picked, setPicked] = useState({});
  const [picking, setPicking] = useState(null);
  // The company's other offices and laboratories, and where this shipment
  // goes: nowhere yet (''), a custom address ('custom') or one of them (its id).
  const [locations, setLocations] = useState([]);
  const [to, setTo] = useState('');
  const toLocation = locations.find((location) => String(location.id) === to) ?? null;
  // A custom address: who gets it, the address as it stands in the form
  // (`placeId`: the suggestion it was last filled from), the countries the
  // form offers, and what an address needs in the one chosen.
  const [recipient, setRecipient] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [place, setPlace] = useState({ country: '', line1: '', city: '', postcode: '', placeId: '' });
  const [line2, setLine2] = useState('');
  const [countries, setCountries] = useState([]);
  const [addressForm, setAddressForm] = useState(null);
  const [checking, setChecking] = useState(false);
  // The form as it stood when XOmics did not know its postcode: going on with
  // it unchanged says the postcode is right.
  const unknownPostcode = useRef('');
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [invalid, setInvalid] = useState(null);

  useEffect(() => {
    let active = true;
    loadProducts()
      .then((list) => { if (active) setProducts(list.filter((product) => product.shippingWeightGrams !== null)); })
      .catch((requestError) => { if (active) setError(errorMessage(requestError)); });
    // The address form opens on the office's own country.
    loadShipmentCountries()
      .then(({ countries: list, officeCountry }) => {
        if (!active) return;
        setCountries(list);
        if (list.some((country) => country.code === officeCountry)) {
          setPlace((current) => (current.country ? current : { ...current, country: officeCountry }));
        }
      })
      .catch((requestError) => { if (active) setError(errorMessage(requestError)); });
    loadShipmentLocations()
      .then((list) => { if (active) setLocations(list); })
      .catch((requestError) => { if (active) setError(errorMessage(requestError)); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setAddressForm(null);
    if (place.country) loadShipmentAddressForm(place.country).then((form) => { if (active) setAddressForm(form); }).catch(() => {});
    return () => { active = false; };
  }, [place.country]);

  // The countries to choose from: every country, with the form's own should a
  // chosen suggestion be in one the list does not have.
  const choices = useMemo(() => countryChoices(
    place.country && !countries.some((country) => country.code === place.country)
      ? [...countries, { code: place.country }] : countries,
  ), [countries, place.country]);
  const countryName = useMemo(() => countryNamer(), []);
  // A postcode was searched by and chosen: where it is, for the street to be
  // looked for around it, until the postcode or the country is changed by hand.
  const nearby = useRef(null);
  const searchAddresses = useCallback((text) => searchShipmentAddresses(text, place.country,
    nearby.current?.country === place.country ? { latitude: nearby.current.latitude, longitude: nearby.current.longitude } : null), [place.country]);
  // What was said about the address as it stood goes once it is changed.
  const setField = (field, value) => {
    if (field === 'postcode' || field === 'country') nearby.current = null;
    setPlace((current) => ({ ...current, [field]: value }));
    setInvalid((current) => (current?.field === 'address' ? null : current));
  };
  // A chosen suggestion fills the form with what it has. A postcode searched
  // by and chosen stays the form's when the address chosen after it, in the
  // same country, has none of its own (a building Google knows by name only).
  // The flat typed in front of a building goes to the second line.
  const chooseAddress = (parts, suggestion) => {
    const squeezed = (text) => String(text || '').replace(/[\s-]+/g, '').toUpperCase();
    const byPostcode = Boolean(parts.postalCode) && squeezed(place.line1) === squeezed(parts.postalCode);
    const kept = nearby.current?.country === parts.countryCode ? nearby.current : null;
    setPlace({
      country: parts.countryCode, line1: parts.line1 || '', city: parts.city || (kept ? place.city : ''),
      postcode: parts.postalCode || (kept ? place.postcode : ''), placeId: parts.placeId,
    });
    nearby.current = byPostcode ? { country: parts.countryCode, latitude: parts.latitude, longitude: parts.longitude } : kept;
    const flat = parts.line2 || suggestion?.flat;
    if (flat) setLine2((current) => current || flat);
    setInvalid((current) => (current?.field === 'address' ? null : current));
  };

  useEffect(() => {
    // Escape leaves what is on top: the form that asks which ones, else the shipment.
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      if (picking !== null) setPicking(null); else onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose, picking]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (products ?? []).filter((product) => needle === ''
      || product.name.toLowerCase().includes(needle) || product.code.toLowerCase().includes(needle));
  }, [products, query]);
  // Whether a product is sent by combination: known once what the office has of it is read.
  const byCombination = (product) => product.hasOptions && stocks[product.id]?.stockPerCombination !== false;
  // What is chosen of a product with choices, in the catalogue's order.
  const chosenOf = (product) => (stocks[product.id]?.combinations ?? [])
    .filter((row) => (picked[product.id]?.[row.sku] ?? 0) > 0)
    .map((row) => ({ ...row, quantity: picked[product.id][row.sku] }));
  const lines = (products ?? []).flatMap((product) => {
    if (byCombination(product)) {
      return chosenOf(product).map((row) => ({ productId: product.id, quantity: row.quantity, options: row.selection }));
    }
    const quantity = quantities[product.id] ?? 0;
    return quantity > 0 ? [{ productId: product.id, quantity }] : [];
  });
  const units = lines.reduce((sum, line) => sum + line.quantity, 0);
  const set = (field, value) => setRecipient((current) => ({ ...current, [field]: value }));
  const change = (productId, delta) => {
    setInvalid(null);
    setQuantities((current) => ({ ...current, [productId]: Math.max(0, (current[productId] ?? 0) + delta) }));
  };

  // A product with choices asks which ones; one that only takes words (an
  // engraving) is counted like any other.
  async function choose(product) {
    setError('');
    try {
      const stock = stocks[product.id] ?? await loadShipmentProductStock(product.id);
      setStocks((had) => ({ ...had, [product.id]: stock }));
      if (stock.stockPerCombination) setPicking(product); else change(product.id, 1);
    } catch (requestError) {
      setError(errorMessage(requestError));
    }
  }

  // What is wrong is said where it is, with the box it is about ready to type in.
  function reveal(problem) {
    setInvalid(problem);
    if (problem.field.startsWith('ns-')) document.getElementById(problem.field)?.focus();
  }

  // From the products to where they go: not with nothing in the parcel.
  function toDestination(event) {
    event.preventDefault();
    if (lines.length === 0) { setInvalid({ field: 'products', message: tx('ui.shipments.need_products') }); return; }
    setInvalid(null);
    setStep('destination');
  }

  // On to the carriers. One of the company's own offices or laboratories goes
  // as the portal keeps it: who it is and where it is are not asked. A custom
  // address, with who gets it, is checked as it stands in the form.
  async function next(event) {
    event.preventDefault();
    if (checking || to === '') return;
    if (toLocation !== null) {
      if (!reachable(toLocation)) return;
      setInvalid(null);
      setError('');
      setDraft({
        email: toLocation.email,
        firstName: toLocation.name,
        lastName: null,
        phone: toLocation.phone,
        address: { ...toLocation.address },
        items: lines,
      });
      return;
    }
    if (recipient.email.trim() === '') { reveal({ field: 'ns-email', message: tx('ui.shipments.need_email') }); return; }
    if (recipient.phone.trim() === '') { reveal({ field: 'ns-phone', message: tx('ui.shipments.need_phone') }); return; }
    setInvalid(null);
    setError('');
    setChecking(true);
    const written = JSON.stringify(place);
    try {
      // It comes back with its region and its point on the map, or with what
      // is wrong with it.
      const checked = await checkShipmentAddress({
        countryCode: place.country,
        line1: place.line1,
        city: place.city,
        // A country whose addresses have no postcode is not asked for one.
        postalCode: addressForm?.postcode === 'none' ? '' : place.postcode,
        placeId: place.placeId || undefined,
        // Staff were told this postcode is not known, and went on with the form as it stood.
        postcodeAccepted: unknownPostcode.current === written || undefined,
      });
      setDraft({
        email: recipient.email.trim(),
        firstName: recipient.firstName.trim() || null,
        lastName: recipient.lastName.trim() || null,
        phone: recipient.phone.trim() || null,
        address: { ...checked, line2: line2.trim() || null },
        items: lines,
      });
    } catch (requestError) {
      if (requestError?.code === 'ADDRESS_POSTCODE_UNKNOWN') unknownPostcode.current = written;
      reveal({ field: 'address', message: errorMessage(requestError) });
    } finally {
      setChecking(false);
    }
  }

  const count = <span>{tx('ui.shipments.items_selected', { count: units })}</span>;
  const back = (to) => (
    <button type="button" className="xo-btn-2" onClick={() => { setInvalid(null); setError(''); to(); }}>
      <ArrowLeft aria-hidden="true" /> {tx('ui.shipments.back')}
    </button>
  );
  const heading = (number, title, hint) => (
    <div className="xo-product-form-heading"><span>{number}</span><div><h3>{title}</h3><p>{hint}</p></div></div>
  );

  return (<>
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="xo-product-editor is-new-shipment" role="dialog" aria-modal="true"
        aria-labelledby="new-shipment-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span>{tx('ui.nav.shipments_7a70be8a')}</span>
            <h2 id="new-shipment-title">{tx('ui.shipments.new_shipment')}</h2>
            <p>{draft === null ? tx('ui.shipments.new_shipment_subtitle') : tx('ui.shipments.ship_to', {
              name: [draft.firstName, draft.lastName].filter(Boolean).join(' ') || draft.email,
              place: [draft.address.city, draft.address.countryCode].filter(Boolean).join(', '),
            })}</p>
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx('ui.common.cancel')} onClick={onClose}><X aria-hidden="true" /></button>
        </header>

        {draft !== null && (
          <div className="xo-product-editor-form">
            <div className="xo-product-editor-scroll">
              <ShippingComposer subject={{ subjectType: 'freeform', draft }} onDone={onCreated} />
            </div>
            <footer>{back(() => setDraft(null))}{count}</footer>
          </div>
        )}

        {draft === null && step === 'products' && (
          <form onSubmit={toDestination} className="xo-product-editor-form">
            <div className="xo-product-editor-scroll is-one-step">
              <section className="xo-product-form-section xo-new-shipment-step is-products">
                {heading(1, tx('ui.shipments.step_products'), tx('ui.shipments.step_products_hint'))}
                <SearchField value={query} onChange={setQuery}
                  placeholder={tx('ui.shipments.search_products')} label={tx('ui.shipments.search_products')} />
                <div className="xo-new-shipment-products" aria-busy={products === null}>
                  {products === null && Array.from({ length: 6 }, (_, index) => (
                    <div key={index} className="is-loading" aria-hidden="true"><i /><i /><i /></div>
                  ))}
                  {visible.map((product) => {
                    if (byCombination(product)) {
                      const chosen = chosenOf(product);
                      const chosenCount = chosen.reduce((sum, row) => sum + row.quantity, 0);
                      return (
                        // Which ones is asked in a form of its own; what is chosen is said under the name.
                        <div key={product.id} className={chosenCount > 0 ? 'is-selected' : ''} onClick={() => choose(product)}>
                          {product.mainImageThumbnailUrl ? <img src={product.mainImageThumbnailUrl} alt="" /> : <span className="xo-new-shipment-thumb" />}
                          <span>
                            <strong>{product.name}</strong>
                            <small>{chosenCount > 0
                              ? chosen.map((row) => `${row.quantity} × ${row.label}`).join(', ')
                              : `${product.code}${product.isActive ? '' : ` · ${tx('ui.shipments.off_sale')}`}`}</small>
                          </span>
                          <span className="xo-new-shipment-quantity">
                            <b>{chosenCount}</b>
                            <button type="button" className="xo-btn-2 xo-new-shipment-which" aria-label={`${tx('ui.shipments.choose_which')}: ${product.name}`}
                              onClick={(event) => { event.stopPropagation(); choose(product); }}>{tx('ui.shipments.choose_which')}</button>
                          </span>
                        </div>
                      );
                    }
                    const quantity = quantities[product.id] ?? 0;
                    return (
                      // A click on the row adds one; the buttons change the count either way.
                      <div key={product.id} className={quantity > 0 ? 'is-selected' : ''} onClick={() => change(product.id, 1)}>
                        {product.mainImageThumbnailUrl ? <img src={product.mainImageThumbnailUrl} alt="" /> : <span className="xo-new-shipment-thumb" />}
                        <span>
                          <strong>{product.name}</strong>
                          <small>{product.code}{product.isActive ? '' : ` · ${tx('ui.shipments.off_sale')}`}</small>
                        </span>
                        <span className="xo-new-shipment-quantity">
                          <button type="button" className="xo-icon-btn" disabled={quantity === 0}
                            aria-label={`- ${product.name}`} onClick={(event) => { event.stopPropagation(); change(product.id, -1); }}><Minus aria-hidden="true" /></button>
                          <b>{quantity}</b>
                          <button type="button" className="xo-icon-btn"
                            aria-label={`+ ${product.name}`} onClick={(event) => { event.stopPropagation(); change(product.id, 1); }}><Plus aria-hidden="true" /></button>
                        </span>
                      </div>
                    );
                  })}
                </div>
                {invalid?.field === 'products'
                  && <p className="xomics-field-error" role="alert">{invalid.message}</p>}
                {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
              </section>
            </div>
            <footer>
              {count}
              <div>
                <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.common.cancel')}</button>
                <button type="submit" className="xo-btn">{tx('ui.shipments.continue')}</button>
              </div>
            </footer>
          </form>
        )}

        {draft === null && step === 'destination' && (
          <form onSubmit={next} className="xo-product-editor-form">
            <div className="xo-product-editor-scroll">
              <section className="xo-product-form-section">
                {heading(2, tx('ui.shipments.step_destination'), tx('ui.shipments.step_destination_hint'))}
                {/* One list for where it goes: a custom address first, then the
                    company's other offices and laboratories, however many there are. */}
                <XomicsSelect aria-label={tx('ui.shipments.step_destination')} value={to}
                  onChange={(event) => { setTo(event.target.value); setInvalid(null); setError(''); }}>
                  <option value="">{tx('ui.common.select')}</option>
                  <option value="custom">{tx('ui.shipments.to_custom')}</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {`${location.name}, ${location.address.city} · ${tx(`ui.values.location_${location.kind}`)}`}
                    </option>
                  ))}
                </XomicsSelect>
                {/* One of the company's own: shown as the portal keeps it, not asked for. */}
                {toLocation !== null && (
                  <div className="xo-new-shipment-destination">
                    <span>{toLocation.kind === 'laboratory' ? <FlaskConical aria-hidden="true" /> : <Building2 aria-hidden="true" />}</span>
                    <div>
                      <strong>{toLocation.name}</strong>
                      <small>{`${tx(`ui.values.location_${toLocation.kind}`)} · ${countryName(toLocation.address.countryCode)}`}</small>
                      <p>{[toLocation.address.line1, toLocation.address.line2,
                        [toLocation.address.postalCode, toLocation.address.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</p>
                      <p>{[toLocation.email, toLocation.phone].filter(Boolean).join(' · ')}</p>
                      {!reachable(toLocation)
                        && <p className="xomics-field-error" role="alert">{tx('ui.shipments.to_location_incomplete')}</p>}
                    </div>
                  </div>
                )}
                {/* A custom address: where, then who gets it. */}
                {to === 'custom' && (
                  <div className="xo-product-form-grid">
                    <div className="xo-product-field-wide"><Field id="ns-country" label={tx('ui.org.country')}>
                      <XomicsSelect {...fieldAria('ns-country')} value={place.country} onChange={(event) => setField('country', event.target.value)}>
                        {choices.map((country) => <option key={country.code} value={country.code}>{country.name}</option>)}
                      </XomicsSelect>
                    </Field></div>
                    <Field id="ns-first-name" label={tx('ui.shipments.first_name')}>
                      <input {...fieldAria('ns-first-name')} value={recipient.firstName} onChange={(event) => set('firstName', event.target.value)} />
                    </Field>
                    <Field id="ns-last-name" label={tx('ui.shipments.last_name')}>
                      <input {...fieldAria('ns-last-name')} value={recipient.lastName} onChange={(event) => set('lastName', event.target.value)} />
                    </Field>
                    {/* The address line doubles as the search: what is typed stays
                        as typed, and a suggestion chosen from under it fills the form. */}
                    <div className="xo-product-field-wide"><Field id="ns-line1" label={tx('ui.locations.address_line1')}>
                      <AddressPicker inputId="ns-line1" search={searchAddresses} details={loadShipmentPlaceParts}
                        value={place.line1} onType={(text) => setField('line1', text)} onSelect={chooseAddress} />
                    </Field></div>
                    <div className="xo-product-field-wide"><Field id="ns-line2" label={tx('ui.shipments.address_line2')}>
                      <input {...fieldAria('ns-line2')} value={line2} onChange={(event) => setLine2(event.target.value)} />
                    </Field></div>
                    <Field id="ns-city" label={tx('ui.locations.city')}>
                      <input {...fieldAria('ns-city')} value={place.city} onChange={(event) => setField('city', event.target.value)} />
                    </Field>
                    {/* A country whose addresses have no postcode is not asked for one. */}
                    {addressForm?.postcode !== 'none' && (
                      <Field id="ns-postal-code" label={tx('ui.locations.postal_code')}>
                        <input {...fieldAria('ns-postal-code')} value={place.postcode} placeholder={addressForm?.postcodeExample || ''}
                          onChange={(event) => setField('postcode', event.target.value)} />
                      </Field>
                    )}
                    {invalid?.field === 'address'
                      && <p className="xomics-field-error xo-product-field-wide" role="alert">{invalid.message}</p>}
                    {/* Who gets it: the shipment is recorded under the email, and the carrier needs the phone. */}
                    <Field id="ns-email" label={tx('ui.shipments.email')} hint={tx('ui.shipments.step_recipient_hint')}
                      error={invalid?.field === 'ns-email' ? invalid.message : undefined}>
                      <input {...fieldAria('ns-email', { hint: true, error: invalid?.field === 'ns-email' })} type="email"
                        value={recipient.email} onChange={(event) => set('email', event.target.value)} />
                    </Field>
                    <Field id="ns-phone" label={tx('ui.shipments.phone')} hint={tx('ui.shipments.phone_hint')}
                      error={invalid?.field === 'ns-phone' ? invalid.message : undefined}>
                      <input {...fieldAria('ns-phone', { hint: true, error: invalid?.field === 'ns-phone' })} type="tel"
                        value={recipient.phone} onChange={(event) => set('phone', event.target.value)} />
                    </Field>
                  </div>
                )}
              </section>
              {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
            </div>
            <footer>
              {back(() => setStep('products'))}
              {count}
              <div>
                <button type="submit" className="xo-btn"
                  disabled={checking || to === '' || (toLocation !== null && !reachable(toLocation))}>{tx('ui.shipments.continue')}</button>
              </div>
            </footer>
          </form>
        )}
      </section>
    </div>
    {picking !== null && (
      <WhichOnes product={picking} stock={stocks[picking.id]} picked={picked[picking.id] ?? {}}
        onClose={() => setPicking(null)}
        onDone={(chosen) => {
          setPicked((had) => ({ ...had, [picking.id]: chosen }));
          setInvalid(null);
          setPicking(null);
        }} />
    )}
  </>);
}
